// Portfolio Administration v1.0 PR 5 — sticky header for the Vendor
// registry detail page. Renders title + status pill + type badge.
// Global actions (Edit / Change status / Archive) are gated by the
// E-VM / E5 / ADMIN role codes per spec §14.

import { useState } from "react";
import { Badge, Button, Card, OverflowMenu } from "@/design-system/components/ui";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useUpdateVendorStatus,
  useVendorPortalAccount,
} from "@/hooks/portfolioAdmin/useRegistries";
import { RegistryStatusPill } from "@/components/portfolioAdmin/registry/RegistryStatusPill";
import { NeedsAttentionBadge } from "@/components/portalAbsorption/NeedsAttentionBadge";
import { VendorArchiveConfirmModal } from "./VendorArchiveConfirmModal";

export interface VendorDetailHeaderProps {
  vendor: VendorDto;
}

const STATUS_TRANSITIONS = [
  "prospect",
  "onboarding",
  "active",
  "suspended",
  "terminated",
];

export function VendorDetailHeader({ vendor }: VendorDetailHeaderProps) {
  const auth = useAuth();
  const toast = useToast();
  const canManage =
    auth.hasRoleCode("E1-VM") ||
    auth.hasRoleCode("E5") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Vendors.Manage");
  const update = useUpdateVendorStatus(vendor.id);
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(vendor.status);
  const [reason, setReason] = useState("");
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const isArchived = !!vendor.isArchived;
  // Portal absorption (WS-D1) — pending portal submissions surface as a
  // needs-attention badge next to the status pill.
  const portalQuery = useVendorPortalAccount(vendor.id);

  const submit = async () => {
    try {
      await update.mutateAsync({ toStatus: next, reason: reason || null });
      toast.success("Vendor status updated.");
      setEditing(false);
      setReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card
      className="flex flex-col gap-space-2"
      data-testid="vendor-detail-header"
    >
      <div className="flex items-start justify-between gap-space-3">
        <div className="flex flex-col gap-space-1">
          <h1 className="font-display font-bold text-h1 m-0">{vendor.name}</h1>
          <div className="flex items-center gap-space-2">
            <RegistryStatusPill status={vendor.status} />
            {isArchived ? (
              <Badge status="archived" data-testid="vendor-archived-badge">
                Archived
              </Badge>
            ) : null}
            <NeedsAttentionBadge portalUserId={portalQuery.data?.portalUserId} />
            <span className="text-small text-gray-mid">
              {vendor.vendorType ?? "—"}
            </span>
          </div>
        </div>
        {canManage ? (
          <div className="flex items-center gap-space-2">
            {!isArchived ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setEditing((v) => !v)}
                data-testid="vendor-change-status"
              >
                Change status
              </Button>
            ) : null}
            <OverflowMenu
              label="More actions"
              data-testid="vendor-more-actions"
              items={[
                isArchived
                  ? {
                      label: "Unarchive vendor",
                      onSelect: () => setArchiveModalOpen(true),
                    }
                  : {
                      label: "Archive vendor",
                      destructive: true,
                      onSelect: () => setArchiveModalOpen(true),
                    },
              ]}
            />
          </div>
        ) : null}
      </div>
      {editing ? (
        <div className="flex flex-wrap items-end gap-space-2 border-t border-border-light pt-space-2">
          <div className="flex flex-col gap-space-1">
            <label className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              New status
            </label>
            <select
              className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="vendor-status-select"
            >
              {STATUS_TRANSITIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-space-1 flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              Reason (audit note)
            </label>
            <input
              type="text"
              className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="vendor-status-reason"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={update.isPending}
            data-testid="vendor-status-submit"
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="tertiary"
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        </div>
      ) : null}
      <VendorArchiveConfirmModal
        open={archiveModalOpen}
        vendor={vendor}
        mode={isArchived ? "unarchive" : "archive"}
        onClose={() => setArchiveModalOpen(false)}
      />
    </Card>
  );
}
